import React, { useState } from 'react';
import { Form, Input, InputNumber, Radio, Button, message } from 'antd';
import './Order-form.scss';

const SALES_TAX = 10;

const vickiKioskPricing = [
    {
        unitName: 'vickiSmartStore',
        label: 'VICKI Smart Store with Zebra Badge Scanner & CV Nano Camera',
        rules: [{ required: true, message: 'Field is required' }],
        retailPrice: 10895,
        wholesalePrice: 8927,
    },
    {
        unitName: 'brandedGraphicsFront',
        label: 'Branded Graphics - Front',
        rules: [{ required: true, message: 'Field is required' }],
        retailPrice: 200,
        wholesalePrice: 50,
    },
    {
        unitName: 'brandedGraphicsWrapped',
        label: 'Branded Graphics – Wrapped',
        rules: [{ required: true, message: 'Field is required' }],
        retailPrice: 450,
        wholesalePrice: 250,
    },
    {
        unitName: 'onsiteInstallationAndTraining',
        label: 'Onsite Installation & Training',
        rules: [{ required: true, message: 'Field is required' }],
        retailPrice: 750,
        wholesalePrice: 250,
    },
    {
        unitName: 'flatRateShipping',
        label: 'Flat Rate Shipping',
        rules: [
            { required: true, message: 'Field is required' },
            ({ getFieldValue }) => ({
                validator(_, value) {
                    if (!value || getFieldValue('flatRateShipping') >= 3) {
                        return Promise.resolve();
                    }
                    return Promise.reject(new Error('Min value is 3'));
                },
            }),
        ],
        retailPrice: null,
        wholesalePrice: 500,
    },
    {
        unitName: 'softwareAndSalesSupport',
        label: 'Software & Sales Support - Billed Monthly',
        rules: [{ required: true, message: 'Field is required' }],
        retailPrice: 29.95,
        wholesalePrice: 24.95,
    },
];

const formatPrice = (price) => {
    if (price === null) return 'n/a';
    if (price === 0) return '';
    return price.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const getUnitPrice = (unit, kioskQty) => {
    if (!kioskQty) return 0;
    return kioskQty < 3 ? (unit.retailPrice || 0) : (unit.wholesalePrice || 0);
};

const getUnitTotal = (unit, qty, kioskQty) => {
    if (!qty || !kioskQty) return 0;
    const price = getUnitPrice(unit, kioskQty);
    return price * qty;
};

function OrderForm({ accessToken }) {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();

    const kioskQty = Form.useWatch('vickiSmartStore', form);
    const qty_vickiSmartStore = Form.useWatch('vickiSmartStore', form);
    const qty_brandedGraphicsFront = Form.useWatch('brandedGraphicsFront', form);
    const qty_brandedGraphicsWrapped = Form.useWatch('brandedGraphicsWrapped', form);
    const qty_onsiteInstallationAndTraining = Form.useWatch('onsiteInstallationAndTraining', form);
    const qty_flatRateShipping = Form.useWatch('flatRateShipping', form);
    const qty_softwareAndSalesSupport = Form.useWatch('softwareAndSalesSupport', form);

    const watchedQtys = {
        vickiSmartStore: qty_vickiSmartStore,
        brandedGraphicsFront: qty_brandedGraphicsFront,
        brandedGraphicsWrapped: qty_brandedGraphicsWrapped,
        onsiteInstallationAndTraining: qty_onsiteInstallationAndTraining,
        flatRateShipping: qty_flatRateShipping,
        softwareAndSalesSupport: qty_softwareAndSalesSupport,
    };

    const calculateSubtotal = () => {
        const formData = form.getFieldsValue();
        const kiosk = formData['vickiSmartStore'];
        return vickiKioskPricing.reduce((sum, unit) => {
            const qty = formData[unit.unitName];
            return sum + getUnitTotal(unit, qty, kiosk);
        }, 0);
    };

    const onFinish = async (formData) => {
        setLoading(true);
        const kiosk = formData['vickiSmartStore'];

        formData.products = vickiKioskPricing
            .filter(unit => formData[unit.unitName])
            .map(unit => {
                const qty = formData[unit.unitName];
                const product = {
                    name: unit.label,
                    quantity: qty,
                    price: getUnitPrice(unit, kiosk),
                    total: getUnitTotal(unit, qty, kiosk),
                };
                delete formData[unit.unitName];
                return product;
            });

        if (formData.invoiceAddress) {
            formData.address = formData.invoiceAddress;
            delete formData.invoiceAddress;
        }

        const subtotal = calculateSubtotal();
        formData.salesTaxPercent = SALES_TAX;
        formData.salesTaxValue = subtotal * (SALES_TAX / 100);
        formData.total = subtotal * (1 + SALES_TAX / 100);

        try {
            const response = await fetch('https://viatouchmedia-test.apigee.net/loyalty/microsite/order?to=viktor.kazmirchuk@oril.co', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'access_token': accessToken,
                },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Something went wrong!');
            }

            messageApi.success('Order form successfully submitted!');
            form.resetFields();
        } catch (err) {
            messageApi.error('Failed to submit Order form: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={'order-form-wrapper'}>
            {contextHolder}
            <Form
                form={form}
                layout="horizontal"
                labelAlign="left"
                labelWrap
                colon={false}
                labelCol={{ span: 7 }}
                wrapperCol={{ span: 17 }}
                scrollToFirstError
                onFinish={onFinish}
                size="large"
            >
                {/* Quote Information */}
                <div className={'form-section'}>
                    <div className={'form-section-title'}>ViaTouch Quote Information</div>
                    <div className={'form-section-body'}>
                        <Form.Item label="Quote Number" name="quoteNumber" initialValue="VTM-Planet Fitness"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <Input disabled />
                        </Form.Item>
                        <Form.Item label="Group Name" name="groupName"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <Input placeholder="Enter group name" />
                        </Form.Item>
                        <Form.Item label="Group Number (G0###)" name="groupNumber"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <Input placeholder="e.g. G0123" />
                        </Form.Item>
                        <Form.Item label="Shipping Address" name="shippingAddress"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <Input placeholder="Full shipping address" />
                        </Form.Item>
                        <Form.Item label="Delivery Name & Contact" name="deliveryNameAndContact"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <Input placeholder="Name and phone number" />
                        </Form.Item>
                        <Form.Item label="Payment Terms" name="paymentTerms" initialValue="Net 30"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <Input disabled />
                        </Form.Item>
                        <Form.Item label="Shipping Terms" name="shippingTerms" initialValue="F.O.B. Origin"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <Input disabled />
                        </Form.Item>
                        <Form.Item label="Total Quantity of VICKIs" name="totalQuantity"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <InputNumber min={1} placeholder="0" style={{ width: '120px' }} />
                        </Form.Item>
                        <div className={'form-notes'}>
                            <p>* Quotes are valid for 30 days after receipt</p>
                            <p>* Training dates must be agreed upon between parties prior to shipment delivery</p>
                            <p>* Delivery takes 4–6 weeks from Order Placement</p>
                        </div>
                    </div>
                </div>

                {/* Pricing Reference Table */}
                <div className={'form-section'}>
                    <div className={'form-section-title'}>VICKI Kiosk Pricing</div>
                    <div className={'form-section-body'}>
                        <div className={'pricing-table'}>
                            <div className={'pricing-row pricing-header'}>
                                <div className={'pricing-col'}>Product Description</div>
                                <div className={'pricing-col pricing-col--narrow'}>Retail Price</div>
                                <div className={'pricing-col pricing-col--narrow'}>PF Price (3+ Kiosks)</div>
                            </div>
                            {vickiKioskPricing.map(unit => (
                                <div key={unit.unitName} className={'pricing-row'}>
                                    <div className={'pricing-col'}>{unit.label}</div>
                                    <div className={'pricing-col pricing-col--narrow'}>{formatPrice(unit.retailPrice)}</div>
                                    <div className={'pricing-col pricing-col--narrow'}>{formatPrice(unit.wholesalePrice)}</div>
                                </div>
                            ))}
                        </div>
                        <div className={'form-notes'}>
                            <p>* Equipment cost includes badge scanner and built-in camera (valued at $900) for $433</p>
                            <p>* Front graphics required. Full wrap recommended for free-standing units</p>
                            <p>* Flat rate shipping: $500 or less per machine depending on shipping zone</p>
                            <p>* Onsite installation and training: $250 per machine</p>
                        </div>
                    </div>
                </div>

                {/* Order Table */}
                <div className={'form-section'}>
                    <div className={'form-section-title'}>Order — Products & Services</div>
                    <div className={'form-section-body'}>
                        <div className={'pricing-table'}>
                            <div className={'pricing-row pricing-header'}>
                                <div className={'pricing-col'}>Product Description</div>
                                <div className={'pricing-col pricing-col--narrow'}>Quantity</div>
                                <div className={'pricing-col pricing-col--narrow'}>Unit Price</div>
                                <div className={'pricing-col pricing-col--narrow'}>Total</div>
                            </div>
                            {vickiKioskPricing.map(unit => (
                                <div key={unit.unitName} className={'pricing-row'}>
                                    <div className={'pricing-col'}>{unit.label}</div>
                                    <div className={'pricing-col pricing-col--narrow'}>
                                        <Form.Item name={unit.unitName} rules={unit.rules} noStyle>
                                            <InputNumber min={0} placeholder="0" style={{ width: '80px' }} />
                                        </Form.Item>
                                    </div>
                                    <div className={'pricing-col pricing-col--narrow'}>
                                        {watchedQtys[unit.unitName] && kioskQty
                                            ? formatPrice(getUnitPrice(unit, kioskQty))
                                            : '—'}
                                    </div>
                                    <div className={'pricing-col pricing-col--narrow'}>
                                        {watchedQtys[unit.unitName] && kioskQty
                                            ? formatPrice(getUnitTotal(unit, watchedQtys[unit.unitName], kioskQty))
                                            : '—'}
                                    </div>
                                </div>
                            ))}
                            <div className={'pricing-row pricing-subtotal'}>
                                <div className={'pricing-col'}>Sales Tax ({SALES_TAX}%)</div>
                                <div className={'pricing-col pricing-col--narrow'}></div>
                                <div className={'pricing-col pricing-col--narrow'}></div>
                                <div className={'pricing-col pricing-col--narrow'}>
                                    {formatPrice(calculateSubtotal() * (SALES_TAX / 100))}
                                </div>
                            </div>
                            <div className={'pricing-row pricing-total'}>
                                <div className={'pricing-col'}>Total</div>
                                <div className={'pricing-col pricing-col--narrow'}></div>
                                <div className={'pricing-col pricing-col--narrow'}></div>
                                <div className={'pricing-col pricing-col--narrow'}>
                                    {formatPrice(calculateSubtotal() * (1 + SALES_TAX / 100))}
                                </div>
                            </div>
                        </div>
                        <div className={'form-notes'}>
                            <p>* Travel & expenses included on final invoice once installation is complete. Will not exceed $500.00 per location.</p>
                        </div>
                        <div className={'warranty-text'}>
                            Product Hardware Warranty Term is 12 months self-parts replacement beginning on the date of
                            delivery. Software & Sales Support is covered for the life of the unit so long as the monthly
                            service fee is paid. By executing this Order Form, you accept and agree to the terms of the
                            ViaTouch Media – Planet Fitness Master Purchase Agreement.
                        </div>
                    </div>
                </div>

                {/* Invoice To */}
                <div className={'form-section'}>
                    <div className={'form-section-title'}>Invoice To</div>
                    <div className={'form-section-body'}>
                        <Form.Item label="Address" name="invoiceAddress"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <Input placeholder="Invoice address" />
                        </Form.Item>
                        <Form.Item label="Attn" name="attn">
                            <Input placeholder="Attention to" />
                        </Form.Item>
                        <Form.Item label="PO Required on Invoice" name="poRequiredOnInvoice"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <Radio.Group>
                                <Radio value="true">Yes</Radio>
                                <Radio value="false">No</Radio>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item label="PO #" name="po">
                            <Input placeholder="Purchase order number" style={{ width: '200px' }} />
                        </Form.Item>
                    </div>
                </div>

                {/* Customer */}
                <div className={'form-section'}>
                    <div className={'form-section-title'}>Planet Fitness Customer</div>
                    <div className={'form-section-body'}>
                        <Form.Item label="Authorized By" name="by"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <Input placeholder="Signature / authorized name" />
                        </Form.Item>
                        <Form.Item label="Name" name="name"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <Input placeholder="Full name" />
                        </Form.Item>
                        <Form.Item label="Title" name="title"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <Input placeholder="Job title" />
                        </Form.Item>
                    </div>
                </div>

                <div className={'form-submit'}>
                    <Button type="primary" htmlType="submit" loading={loading} size="large">
                        Submit Order
                    </Button>
                </div>
            </Form>
        </div>
    );
}

export default OrderForm;
